// Global Instructions Rule Applied!

import { useCallback } from 'react'
import {
  ContactRecordFields,
  ContactRecord,
  validateContactRecord
} from '../../../state_manager/data_models/contact_record.ts'
import { transformFormDataToContactRecord as transformFormData } from '../utils/contactFormTransformers.ts'

/**
 * Custom hook for managing contact form validation and data transformation
 * Handles form validation and converts display values to database values
 */
export const useContactsForm = () => {
  // Validate required fields
  const validateRequiredFields = useCallback(
    (formData: Record<string, any>): { isValid: boolean; errors: Record<string, string> } => {
      const errors: Record<string, string> = {}

      // Last name is required
      if (
        !formData[ContactRecordFields.contacts__last_name] ||
        formData[ContactRecordFields.contacts__last_name].trim() === ''
      ) {
        errors[ContactRecordFields.contacts__last_name] = 'Last name is required'
      }

      return {
        isValid: Object.keys(errors).length === 0,
        errors
      }
    },
    []
  )

  // Transform form data to ContactRecord format
  const transformFormDataToContactRecord = useCallback((formData: Record<string, any>): ContactRecord => {
    // Clean up: convert empty strings to null for nullable fields
    const nullableFields = [
      ContactRecordFields.contacts__first_name,
      ContactRecordFields.contacts__middle_name,
      ContactRecordFields.contacts__preferred_name,
      ContactRecordFields.contacts__email,
      ContactRecordFields.contacts__email_other,
      ContactRecordFields.contacts__email_private,
      ContactRecordFields.contacts__business_phone,
      ContactRecordFields.contacts__mobile_phone,
      ContactRecordFields.contacts__linked_in_url,
      ContactRecordFields.contacts__address_1,
      ContactRecordFields.contacts__address_2,
      ContactRecordFields.contacts__address_3,
      ContactRecordFields.contacts__address_city_label,
      ContactRecordFields.contacts__address_city_code,
      ContactRecordFields.contacts__address_stateprovince,
      ContactRecordFields.contacts__address_postal_code,
      ContactRecordFields.contacts__address_country_label,
      ContactRecordFields.contacts__address_country_code,
      ContactRecordFields.contacts__photo_url,
      ContactRecordFields.contacts__notes
    ]

    const cleanedData = { ...formData }
    nullableFields.forEach((field) => {
      if (cleanedData[field] === '' || (typeof cleanedData[field] === 'string' && cleanedData[field].trim() === '')) {
        cleanedData[field] = null
      }
    })

    // Ensure nested arrays exist
    if (!cleanedData.prog__current_roles) {
      cleanedData.prog__current_roles = []
    }
    if (!cleanedData.prog__prior_roles) {
      cleanedData.prog__prior_roles = []
    }
    if (!cleanedData.prog__founder_of) {
      cleanedData.prog__founder_of = []
    }
    if (!cleanedData.prog__introductions) {
      cleanedData.prog__introductions = []
    }

    // Generate UUID for contacts__id if not present (required for new contacts)
    const contactId = cleanedData[ContactRecordFields.contacts__id] || crypto.randomUUID()
    if (!cleanedData[ContactRecordFields.contacts__id]) {
      cleanedData[ContactRecordFields.contacts__id] = contactId
    }

    // Transform form data using transformer (handles CompanyTeamEntrySchema conversion)
    const transformed = transformFormData(cleanedData, contactId)

    // Strip internal keys from city/country valueTransformers (used for patch field mapping, not API)
    const internalKeysToStrip = ['cityCode', 'cityName', 'state', 'countryCode']
    internalKeysToStrip.forEach((key) => {
      if (key in transformed) delete transformed[key]
    })

    // Validate the transformed data
    try {
      return validateContactRecord(transformed) as ContactRecord
    } catch (error) {
      throw new Error(`Contact record validation failed: ${error}`)
    }
  }, [])

  return {
    validateRequiredFields,
    transformFormDataToContactRecord
  }
}
